import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Check, ArrowRight as ArrowRightIcon } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useLanguage } from '../contexts/LanguageContext';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Brand accent for active state in this form
const ACCENT = '#FF8C00';

const STEP_IMAGES = [
  'https://customer-assets-wrfwihn1.emergentagent.net/job_construction-global/artifacts/dvk3o6t6_Capture%20d%E2%80%99%C3%A9cran%202026-07-29%20143514.png',
  'https://images.unsplash.com/photo-1505691938895-1758d7feb511?auto=format&fit=crop&w=900&q=70',
  'https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=900&q=70',
  'https://images.unsplash.com/photo-1486325212027-8081e485255e?auto=format&fit=crop&w=900&q=70',
  'https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=900&q=70',
  'https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=900&q=70',
];

const ProjectFormPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { language } = useLanguage();
  const t = (fr, en) => (language === 'fr' ? fr : en);

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    prestation: '',
    surface: '',
    budgetExact: 75000,
    timeline: '',
    landOwnership: '',
    landBuildable: '',
    landSurface: 500,
    firstName: '',
    lastName: '',
    contactEmail: '',
    contactPhone: '',
  });
  const [emailError, setEmailError] = useState('');

  const stepLabels = useMemo(
    () => [
      t('Prestation', 'Service'),
      t('Votre Projet', 'Your Project'),
      t('Financement', 'Financing'),
      t('Délai', 'Timeline'),
      t('Votre Terrain', 'Your Land'),
      t('Coordonnées', 'Contact'),
    ],
    [language] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const prestations = [
    {
      id: 'construire',
      label: t('Construire ma maison et/ou immeuble', 'Build my house and/or building'),
    },
    {
      id: 'renover',
      label: t('Rénover ma maison et/ou mon immeuble', 'Renovate my house and/or building'),
    },
    {
      id: 'energie',
      label: t(
        "Équiper ma maison et/ou mon immeuble (infrastructure énergétique solaire)",
        'Equip my house and/or building (solar energy infrastructure)'
      ),
    },
  ];

  const surfaces = ['50 m²', '70 m²', '90 m²', '120 m²', '150 m²', '200 m²'];
  const timelines = [
    { id: 'now', label: t('Immédiatement', 'Immediately') },
    { id: '<3m', label: t('Moins de 3 mois', 'Less than 3 months') },
    { id: '3-6m', label: t('Entre 3 et 6 mois', '3 to 6 months') },
    { id: '6-12m', label: t('Entre 6 et 12 mois', '6 to 12 months') },
    { id: '>1y', label: t('> 1 an', '> 1 year') },
  ];
  const ownership = [
    { id: 'yes', label: t('Oui', 'Yes') },
    { id: 'no', label: t('Non', 'No') },
    { id: 'pending', label: t("En cours d'acquisition", 'In acquisition') },
  ];
  const buildable = [
    { id: 'yes', label: t('Oui', 'Yes') },
    { id: 'no', label: t('Non', 'No') },
    { id: 'unknown', label: t('Je ne sais pas', "I don't know") },
  ];

  const update = (field, value) => setFormData((p) => ({ ...p, [field]: value }));

  const formatUSD = (n) => `${Number(n).toLocaleString('en-US')} $`;
  const formatM2 = (n) => `${n} m²`;

  const isStepValid = () => {
    switch (step) {
      case 1: return !!formData.prestation;
      case 2: return !!formData.surface;
      case 3: return formData.budgetExact > 0;
      case 4: return !!formData.timeline;
      case 5: return !!formData.landOwnership && !!formData.landBuildable && formData.landSurface > 0;
      case 6: {
        const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail);
        return formData.firstName && formData.lastName && emailOk;
      }
      default: return false;
    }
  };

  const next = () => {
    if (!isStepValid()) {
      if (step === 6) {
        const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail);
        if (!emailOk) {
          setEmailError(t('Veuillez entrer une adresse email valide.', 'Please enter a valid email address.'));
        }
      }
      return;
    }
    setEmailError('');
    if (step < 6) setStep(step + 1);
    else submit();
  };
  const prev = () => step > 1 && setStep(step - 1);

  const submit = async () => {
    try {
      setSubmitting(true);
      const prestationToType = {
        construire: 'house',
        renover: 'renovation',
        energie: 'energy',
      };
      const payload = {
        projectType: prestationToType[formData.prestation] || 'house',
        projectCategory: formData.prestation,
        surface: formData.surface,
        budget: `${formData.budgetExact} USD`,
        budgetExact: Number(formData.budgetExact),
        timeline: formData.timeline,
        landOwnership: formData.landOwnership,
        landBuildable: formData.landBuildable,
        landSurface: Number(formData.landSurface),
        firstName: formData.firstName,
        lastName: formData.lastName,
        contactName: `${formData.firstName} ${formData.lastName}`.trim(),
        contactEmail: formData.contactEmail,
        contactPhone: formData.contactPhone,
        language,
      };
      await axios.post(`${API}/projects`, payload);
      toast({
        title: t('Merci !', 'Thank you!'),
        description: t(
          'Votre demande a été envoyée. Nous vous contacterons sous 48 h.',
          'Your request has been sent. We will contact you within 48 hours.'
        ),
      });
      setTimeout(() => navigate('/'), 1800);
    } catch (err) {
      toast({
        title: t("Échec de l'envoi", 'Submission failed'),
        description: err?.response?.data?.detail || t('Veuillez réessayer.', 'Please try again.'),
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // ---- UI helpers ----
  const StepBadge = ({ index }) => {
    const i = index + 1;
    const completed = step > i;
    const current = step === i;
    return (
      <div className="flex flex-col items-center" data-testid={`stepper-${i}`}>
        <div
          className={`w-9 h-9 md:w-11 md:h-11 rounded-full flex items-center justify-center font-bold text-sm md:text-base transition-all duration-300 ${
            current ? 'text-white shadow-lg' : completed ? 'text-white' : 'bg-gray-200 text-gray-500'
          }`}
          style={{ backgroundColor: current || completed ? ACCENT : undefined }}
        >
          {completed ? <Check size={18} /> : i}
        </div>
        <span
          className={`mt-2 text-xs md:text-sm font-semibold text-center hidden sm:block ${
            current ? 'text-gray-900' : 'text-gray-500'
          }`}
        >
          {stepLabels[index]}
        </span>
      </div>
    );
  };

  const PrimaryBtn = ({ children, onClick, disabled }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || submitting}
      className="inline-flex items-center justify-center gap-2 px-10 py-3 rounded-full font-bold text-white text-base md:text-lg transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg"
      style={{ backgroundColor: disabled ? '#cbd5e1' : ACCENT }}
      data-testid="form-validate-btn"
    >
      {children}
    </button>
  );

  const RadioPill = ({ active, onClick, children, testid }) => (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-6 py-3 rounded-full font-semibold text-sm md:text-base border-2 transition-all duration-300 ${
        active
          ? 'text-white border-transparent shadow-md'
          : 'bg-white text-gray-700 border-gray-200 hover:border-orange-300 hover:bg-orange-50'
      }`}
      style={{ backgroundColor: active ? ACCENT : undefined }}
    >
      {children}
    </button>
  );

  // ---- Step content ----
  const renderStep = () => {
    if (step === 1) {
      return (
        <div className="space-y-7" data-testid="step-1">
          <h2 className="text-2xl md:text-4xl font-bold text-gray-900 leading-tight">
            {t('Quelle prestation souhaitez-vous ?', 'Which service do you want?')}
          </h2>
          <div className="flex flex-col gap-3">
            {prestations.map((p) => {
              const active = formData.prestation === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => update('prestation', p.id)}
                  data-testid={`prestation-${p.id}`}
                  className={`text-left px-6 py-4 rounded-2xl border-2 font-semibold text-sm md:text-base transition-all duration-300 ${
                    active
                      ? 'text-white border-transparent shadow-md'
                      : 'bg-white text-gray-800 border-gray-200 hover:border-orange-300 hover:bg-orange-50'
                  }`}
                  style={{ backgroundColor: active ? ACCENT : undefined }}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
        </div>
      );
    }
    if (step === 2) {
      return (
        <div className="space-y-7" data-testid="step-2">
          <h2 className="text-2xl md:text-4xl font-bold text-gray-900 leading-tight">
            {t('Quelle surface vous faut-il ?', 'What surface do you need?')}
          </h2>
          <div className="flex flex-wrap gap-3">
            {surfaces.map((s) => (
              <RadioPill
                key={s}
                active={formData.surface === s}
                onClick={() => update('surface', s)}
                testid={`surface-${s.replace(/\s/g, '')}`}
              >
                {s}
              </RadioPill>
            ))}
          </div>
        </div>
      );
    }
    if (step === 3) {
      return (
        <div className="space-y-8" data-testid="step-3">
          <h2 className="text-2xl md:text-4xl font-bold text-gray-900 leading-tight">
            {t('Quel est votre budget ?', 'What is your budget?')}
          </h2>
          <div className="space-y-4">
            <div className="flex items-baseline gap-2">
              <span className="text-3xl md:text-5xl font-bold" style={{ color: ACCENT }}>
                {formatUSD(formData.budgetExact)}
              </span>
            </div>
            <input
              type="range"
              min={20000}
              max={500000}
              step={5000}
              value={formData.budgetExact}
              onChange={(e) => update('budgetExact', Number(e.target.value))}
              className="w-full accent-orange-500 cursor-pointer"
              data-testid="budget-slider"
            />
            <div className="flex justify-between text-xs text-gray-500 font-semibold">
              <span>20 000 $</span>
              <span>500 000 $+</span>
            </div>
          </div>
        </div>
      );
    }
    if (step === 4) {
      return (
        <div className="space-y-7" data-testid="step-4">
          <h2 className="text-2xl md:text-4xl font-bold text-gray-900 leading-tight">
            {t('Quand souhaitez-vous être installé ?', 'When do you want to be installed?')}
          </h2>
          <div className="flex flex-wrap gap-3">
            {timelines.map((tl) => (
              <RadioPill
                key={tl.id}
                active={formData.timeline === tl.id}
                onClick={() => update('timeline', tl.id)}
                testid={`timeline-${tl.id}`}
              >
                {tl.label}
              </RadioPill>
            ))}
          </div>
        </div>
      );
    }
    if (step === 5) {
      return (
        <div className="space-y-8" data-testid="step-5">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 leading-tight">
              {t('Êtes-vous propriétaire du terrain ?', 'Are you the owner of the land?')}
            </h2>
            <div className="flex flex-wrap gap-3">
              {ownership.map((o) => (
                <RadioPill
                  key={o.id}
                  active={formData.landOwnership === o.id}
                  onClick={() => update('landOwnership', o.id)}
                  testid={`ownership-${o.id}`}
                >
                  {o.label}
                </RadioPill>
              ))}
            </div>
          </div>

          <div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 leading-tight">
              {t('Votre terrain est-il constructible ?', 'Is your land buildable?')}
            </h2>
            <div className="flex flex-wrap gap-3">
              {buildable.map((b) => (
                <RadioPill
                  key={b.id}
                  active={formData.landBuildable === b.id}
                  onClick={() => update('landBuildable', b.id)}
                  testid={`buildable-${b.id}`}
                >
                  {b.label}
                </RadioPill>
              ))}
            </div>
          </div>

          <div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 leading-tight">
              {t('Quelle est la surface du terrain ?', 'What is the land surface?')}
            </h2>
            <div className="space-y-3">
              <span className="text-2xl md:text-4xl font-bold" style={{ color: ACCENT }}>
                {formData.landSurface <= 100
                  ? t('100 m² et moins', '100 m² or less')
                  : formatM2(formData.landSurface)}
              </span>
              <input
                type="range"
                min={100}
                max={2000}
                step={50}
                value={formData.landSurface}
                onChange={(e) => update('landSurface', Number(e.target.value))}
                className="w-full accent-orange-500 cursor-pointer"
                data-testid="land-surface-slider"
              />
              <div className="flex justify-between text-xs text-gray-500 font-semibold">
                <span>100 m²</span>
                <span>2000 m²+</span>
              </div>
            </div>
          </div>
        </div>
      );
    }
    if (step === 6) {
      return (
        <div className="space-y-5" data-testid="step-6">
          <h2 className="text-2xl md:text-4xl font-bold text-gray-900 leading-tight mb-2">
            {t('Vos coordonnées', 'Your contact details')}
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t('Quel est votre prénom ?', 'What is your first name?')}
              </label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => update('firstName', e.target.value)}
                className="w-full px-5 py-3 rounded-full border-2 border-gray-200 focus:border-orange-500 focus:outline-none transition-colors text-base"
                data-testid="firstname-input"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t('Quel est votre nom ?', 'What is your last name?')}
              </label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => update('lastName', e.target.value)}
                className="w-full px-5 py-3 rounded-full border-2 border-gray-200 focus:border-orange-500 focus:outline-none transition-colors text-base"
                data-testid="lastname-input"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t('Quel est votre email ?', 'What is your email?')}
              </label>
              <input
                type="email"
                value={formData.contactEmail}
                onChange={(e) => {
                  update('contactEmail', e.target.value);
                  setEmailError('');
                }}
                onBlur={() => {
                  if (formData.contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
                    setEmailError(t('Veuillez entrer une adresse email valide.', 'Please enter a valid email address.'));
                  }
                }}
                className={`w-full px-5 py-3 rounded-full border-2 focus:outline-none transition-colors text-base ${
                  emailError ? 'border-red-500' : 'border-gray-200 focus:border-orange-500'
                }`}
                data-testid="email-input"
              />
              {emailError && (
                <p className="mt-2 text-sm text-red-500 font-semibold">{emailError}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t('Quel est votre numéro de téléphone ?', 'What is your phone number?')}
              </label>
              <input
                type="tel"
                value={formData.contactPhone}
                onChange={(e) => update('contactPhone', e.target.value)}
                placeholder="+243 …"
                className="w-full px-5 py-3 rounded-full border-2 border-gray-200 focus:border-orange-500 focus:outline-none transition-colors text-base"
                data-testid="phone-input"
              />
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="pt-[240px] pb-20" data-testid="project-form-page">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-600 hover:text-orange-500 transition-colors mb-6"
            data-testid="back-home-btn"
          >
            <ArrowLeft size={20} />
            <span className="font-semibold">{t("Retour à l'accueil", 'Back to home')}</span>
          </button>

          {/* Stepper */}
          <div className="bg-white rounded-2xl shadow-sm p-5 md:p-7 mb-6" data-testid="stepper">
            <div className="flex items-start justify-between gap-2">
              {stepLabels.map((_, i) => (
                <React.Fragment key={i}>
                  <StepBadge index={i} />
                  {i < stepLabels.length - 1 && (
                    <div
                      className="flex-1 h-1 mt-4 md:mt-5 rounded-full transition-all"
                      style={{ backgroundColor: step > i + 1 ? ACCENT : '#e5e7eb' }}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Two-column card */}
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden grid grid-cols-1 lg:grid-cols-2">
            {/* Image */}
            <div className="relative min-h-[260px] lg:min-h-[560px] order-1 lg:order-1">
              <img
                src={STEP_IMAGES[step - 1]}
                alt={stepLabels[step - 1]}
                className="absolute inset-0 w-full h-full object-cover"
                key={step}
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-black/40 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <span className="inline-block px-3 py-1 rounded-full bg-white/20 backdrop-blur text-xs font-bold tracking-widest uppercase">
                  {t('Étape', 'Step')} {step} / 6
                </span>
                <h3 className="mt-3 text-2xl md:text-3xl font-bold drop-shadow">
                  {stepLabels[step - 1]}
                </h3>
              </div>
            </div>

            {/* Form */}
            <div className="p-6 md:p-10 lg:p-12 order-2 lg:order-2 flex flex-col">
              <div className="flex-1">{renderStep()}</div>

              {/* Navigation */}
              <div className="mt-8 flex items-center justify-between gap-4">
                {step > 1 ? (
                  <button
                    type="button"
                    onClick={prev}
                    className="text-gray-500 hover:text-gray-800 font-semibold inline-flex items-center gap-2"
                    data-testid="form-prev-btn"
                  >
                    <ArrowLeft size={18} />
                    {t('Retour', 'Back')}
                  </button>
                ) : (
                  <span />
                )}
                <PrimaryBtn onClick={next} disabled={!isStepValid()}>
                  {step === 6
                    ? submitting ? t('Envoi…', 'Sending…') : t('Envoyer ma demande', 'Submit')
                    : t('Valider', 'Validate')}
                  {step < 6 && !submitting && <ArrowRightIcon size={18} />}
                </PrimaryBtn>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ProjectFormPage;
